import { useEffect, useState } from "react";
import { AuthTokenRetrieve, LoginButton } from "../Utility/AuthTokenRetrieve";
import { buildApiUrl } from "../Utility/api";
import "../Food/RecipeList.css";
import "./FoodDiary.css";

const windowLabels = ["0-2h", "2-6h", "6-12h", "12-24h"];

const buildSummaryExport = ({
  summaryRows,
  ingredientCandidates,
  tagCandidates,
  activeIngredientCandidates,
  activeTagCandidates,
  toleratedIngredients,
  rangeDays,
}) => {
  const symptomLines = summaryRows.map((summaryRow) => {
    const windowText = windowLabels
      .map((windowLabel) => {
        const meals = summaryRow.windows?.[windowLabel] || [];
        if (!meals.length) {
          return `- ${windowLabel}: none`;
        }

        return `- ${windowLabel}: ${meals
          .map((meal) => `${meal.mealType} (${meal.eatenAt}; ${meal.mealItems || "no items"})`)
          .join(" | ")}`;
      })
      .join("\n");

    const activeMeals = summaryRow.duringActiveMeals?.length
      ? summaryRow.duringActiveMeals
          .map((meal) => `${meal.mealType} (${meal.eatenAt}; ${meal.mealItems || "no items"})`)
          .join(" | ")
      : "none";

    return [
      `${summaryRow.symptomType} | severity ${summaryRow.severity} | started ${summaryRow.startedAt} | ended ${summaryRow.endedAt || "still active"}`,
      summaryRow.notes ? `Notes: ${summaryRow.notes}` : "Notes: none",
      `Meals during active symptom: ${activeMeals}`,
      windowText,
    ].join("\n");
  });

  const formatCandidates = (label, values) =>
    `${label}: ${values.length ? values.map((item) => `${item.name} (${item.count})`).join(", ") : "none"}`;

  return [
    "Food Diary Summary Export",
    "",
    `Range: last ${rangeDays} days`,
    "",
    "Use this as a structured summary for diet/symptom review. Foods before symptom onset are potential triggers. Foods eaten during an active symptom may be aggravators, not causes.",
    "",
    formatCandidates("Trigger candidate ingredients", ingredientCandidates),
    formatCandidates("Trigger candidate tags", tagCandidates),
    formatCandidates("Possible aggravators during active symptoms", activeIngredientCandidates),
    formatCandidates("Active-symptom tags", activeTagCandidates),
    formatCandidates("Frequently tolerated ingredients", toleratedIngredients),
    "",
    "Recent symptom windows:",
    symptomLines.length ? symptomLines.join("\n\n") : "No symptom history yet.",
    "",
    "Prompt suggestion for ChatGPT:",
    "Review this symptom and meal summary. Separate likely triggers from foods eaten during already-active symptoms. Call out repeated ingredient patterns, likely confounders, and suggest cautious elimination or testing ideas without overstating causality.",
  ].join("\n");
};

const FoodDiarySummary = () => {
  const [shouldShow, setShouldShow] = useState();
  const [summaryRows, setSummaryRows] = useState([]);
  const [ingredientCandidates, setIngredientCandidates] = useState([]);
  const [tagCandidates, setTagCandidates] = useState([]);
  const [activeIngredientCandidates, setActiveIngredientCandidates] = useState([]);
  const [activeTagCandidates, setActiveTagCandidates] = useState([]);
  const [toleratedIngredients, setToleratedIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const [rangeDays, setRangeDays] = useState("90");
  const { shouldShow: shouldShowGoogle } = AuthTokenRetrieve();

  useEffect(() => {
    setShouldShow(shouldShowGoogle);
  }, [shouldShowGoogle]);

  const accessToken = localStorage.getItem("lesterAccessToken") || "";

  useEffect(() => {
    if (!shouldShow || !accessToken) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError("");
    fetch(buildApiUrl(`/food/diary/summary?accessToken=${encodeURIComponent(accessToken)}&rangeDays=${encodeURIComponent(rangeDays)}`), {
      cache: "no-cache",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.error) {
          setSummaryRows([]);
          setIngredientCandidates([]);
          setTagCandidates([]);
          setActiveIngredientCandidates([]);
          setActiveTagCandidates([]);
          setToleratedIngredients([]);
          setLoadError(data.error || "Unable to load summary.");
        } else {
          setSummaryRows(data?.recentSymptoms || []);
          setIngredientCandidates(data?.ingredientCandidates || []);
          setTagCandidates(data?.tagCandidates || []);
          setActiveIngredientCandidates(data?.activeSymptomIngredientCandidates || []);
          setActiveTagCandidates(data?.activeSymptomTagCandidates || []);
          setToleratedIngredients(data?.toleratedIngredients || []);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoadError("Unable to load summary.");
        setLoading(false);
      });
  }, [shouldShow, accessToken, rangeDays]);

  const exportText = buildSummaryExport({
    summaryRows,
    ingredientCandidates,
    tagCandidates,
    activeIngredientCandidates,
    activeTagCandidates,
    toleratedIngredients,
    rangeDays,
  });

  const copyExport = async () => {
    try {
      await navigator.clipboard.writeText(exportText);
      setCopyStatus("Summary copied");
    } catch (error) {
      setCopyStatus("Unable to copy summary");
    }
  };

  if (!shouldShow) {
    return (
      <div className="searchBarDiv formViewWrapper FoodDiaryLogin">
        <h2>Food diary</h2>
        <p>Sign in to view private correlation summaries.</p>
        <LoginButton setShouldShow={setShouldShow}></LoginButton>
      </div>
    );
  }

  return (
    <div className="FoodDiaryPage">
      <div className="shoppingListDiv FoodDiaryPanel FoodDiarySummaryPanel">
        <h2 className="FoodDiaryTitle">Symptom summary</h2>
        <p className="FoodDiarySubtitle">
          Recent symptoms with meals grouped by how many hours before the symptom they were eaten.
        </p>
        <div className="FoodDiaryTwoColumn">
          <div className="inputDiv">
            <label>Summary range</label>
            <select
              className="inputBox addInput"
              value={rangeDays}
              onChange={(event) => setRangeDays(event.target.value)}
            >
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="180">Last 180 days</option>
              <option value="365">Last 365 days</option>
              <option value="1095">Last 3 years</option>
            </select>
          </div>
        </div>
        <div className="FoodDiaryActionRow">
          <button className="summaryButton" onClick={copyExport}>
            Copy Summary
          </button>
        </div>
        {copyStatus ? <p className="FoodDiaryStatus">{copyStatus}</p> : null}

        {loading ? <p>Loading summary...</p> : null}
        {!loading && loadError ? <p className="IngredientReviewError">{loadError}</p> : null}
        {!loading && !summaryRows.length ? <p>No symptom history yet.</p> : null}

        {!loading && (ingredientCandidates.length || tagCandidates.length) ? (
          <div className="FoodDiarySummaryWindows">
            <div className="FoodDiaryDetailBlock">
              <strong>Trigger candidate ingredients</strong>
              {ingredientCandidates.length ? (
                ingredientCandidates.map((ingredient) => (
                  <div className="FoodDiaryMealItemHeader" key={ingredient.name}>
                    <span>{ingredient.name}</span>
                    <span>{ingredient.count}</span>
                  </div>
                ))
              ) : (
                <p className="FoodDiaryMealNotes">No ingredient signal yet.</p>
              )}
            </div>
            <div className="FoodDiaryDetailBlock">
              <strong>Trigger candidate tags</strong>
              {tagCandidates.length ? (
                tagCandidates.map((tag) => (
                  <div className="FoodDiaryMealItemHeader" key={tag.name}>
                    <span>{tag.name}</span>
                    <span>{tag.count}</span>
                  </div>
                ))
              ) : (
                <p className="FoodDiaryMealNotes">No tag signal yet.</p>
              )}
            </div>
            <div className="FoodDiaryDetailBlock">
              <strong>Possible aggravators during active symptoms</strong>
              {activeIngredientCandidates.length ? (
                activeIngredientCandidates.map((ingredient) => (
                  <div className="FoodDiaryMealItemHeader" key={ingredient.name}>
                    <span>{ingredient.name}</span>
                    <span>{ingredient.count}</span>
                  </div>
                ))
              ) : (
                <p className="FoodDiaryMealNotes">No repeated foods logged during active symptoms yet.</p>
              )}
              {activeTagCandidates.length ? (
                <div className="FoodDiarySummaryStack">
                  {activeTagCandidates.map((tag) => (
                    <div className="FoodDiaryMealItemHeader" key={tag.name}>
                      <span>{tag.name}</span>
                      <span>{tag.count}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="FoodDiaryDetailBlock">
              <strong>Frequently tolerated ingredients</strong>
              {toleratedIngredients.length ? (
                toleratedIngredients.map((ingredient) => (
                  <div className="FoodDiaryMealItemHeader" key={ingredient.name}>
                    <span>{ingredient.name}</span>
                    <span>{ingredient.count}</span>
                  </div>
                ))
              ) : (
                <p className="FoodDiaryMealNotes">No clear tolerated-food signal yet.</p>
              )}
            </div>
          </div>
        ) : null}

        {summaryRows.map((summaryRow) => (
          <div key={summaryRow.id} className="FoodDiarySummaryCard">
            <div className="FoodDiaryMealItemHeader">
              <strong>{summaryRow.symptomType}</strong>
              <span>Severity {summaryRow.severity}</span>
            </div>
            <div className="FoodDiarySymptomMeta">
              <span>Started: {summaryRow.startedAt}</span>
              <span>Ended: {summaryRow.endedAt || "Still active"}</span>
            </div>
            {summaryRow.notes ? <p className="FoodDiaryMealNotes">{summaryRow.notes}</p> : null}

            <div className="FoodDiarySummaryWindows">
              <div className="FoodDiaryDetailBlock">
                <strong>During active symptom</strong>
                {summaryRow.duringActiveMeals?.length ? (
                  summaryRow.duringActiveMeals.map((meal) => (
                    <div className="FoodDiarySummaryMeal" key={`active-${summaryRow.id}-${meal.id}`}>
                      <div className="FoodDiaryMealItemHeader">
                        <span>{meal.mealType}</span>
                        <span>{meal.eatenAt}</span>
                      </div>
                      <div className="FoodDiarySummaryMealItems">{meal.mealItems || "No items recorded"}</div>
                      {meal.ingredients?.length ? (
                        <div className="FoodDiarySummaryChips">
                          {meal.ingredients.map((ingredient) => (
                            <span className="FoodDiarySummaryChip" key={`active-${meal.id}-${ingredient}`}>
                              {ingredient}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      {meal.tags?.length ? (
                        <div className="FoodDiarySummaryChips">
                          {meal.tags.map((tag) => (
                            <span className="FoodDiarySummaryChip FoodDiarySummaryChipTag" key={`active-${meal.id}-${tag}`}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))
                ) : (
                  <p className="FoodDiaryMealNotes">No meals logged during this active symptom window.</p>
                )}
              </div>
              {windowLabels.map((windowLabel) => (
                <div className="FoodDiaryDetailBlock" key={`${summaryRow.id}-${windowLabel}`}>
                  <strong>{windowLabel}</strong>
                  {summaryRow.windows[windowLabel]?.length ? (
                    summaryRow.windows[windowLabel].map((meal) => (
                      <div className="FoodDiarySummaryMeal" key={`${windowLabel}-${meal.id}`}>
                        <div className="FoodDiaryMealItemHeader">
                          <span>{meal.mealType}</span>
                          <span>{meal.hoursBeforeSymptom}h before</span>
                        </div>
                        <div>{meal.eatenAt}</div>
                        <div className="FoodDiarySummaryMealItems">{meal.mealItems || "No items recorded"}</div>
                        {meal.ingredients?.length ? (
                          <div className="FoodDiarySummaryChips">
                            {meal.ingredients.map((ingredient) => (
                              <span className="FoodDiarySummaryChip" key={`${meal.id}-${windowLabel}-${ingredient}`}>
                                {ingredient}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        {meal.tags?.length ? (
                          <div className="FoodDiarySummaryChips">
                            {meal.tags.map((tag) => (
                              <span className="FoodDiarySummaryChip FoodDiarySummaryChipTag" key={`${meal.id}-${windowLabel}-${tag}`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <p className="FoodDiaryMealNotes">No meals logged in this window.</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {!loading ? (
          <div className="FoodDiaryDetailBlock FoodDiarySummaryExportBlock">
            <strong>Export Preview</strong>
            <pre className="FoodDiaryExportText">{exportText}</pre>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FoodDiarySummary;
